import React from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Check } from "lucide-react";
import { useQuote } from "../QuoteDialog";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_school-printing-hub/artifacts/t08b827e_canon-imageprograf-pro.jpg";

const POINTS = [
  "Indoor & outdoor printing capabilities",
  "Built specifically for K-12 schools",
  "Bundled supplies & finishing tools",
  "Same-day quote on most setups",
];

export default function PrintersHero() {
  const { openQuote } = useQuote();
  return (
    <section
      data-testid="printers-hero-section"
      className="relative isolate overflow-hidden bg-navy"
    >
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Professional wide-format printer producing a school poster"
          className="w-full h-full object-cover opacity-50"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F1F4D] via-[#0F1F4D]/85 to-[#0F1F4D]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F1F4D]/80 via-transparent to-transparent" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-20 pb-24 md:pt-28 md:pb-32 lg:pt-36 lg:pb-40">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white text-xs font-bold uppercase tracking-[0.18em] mb-8 animate-fade-up">
            <span className="w-1.5 h-1.5 rounded-full bg-teal" />
            Print Shop Pro · School Printer Selector
          </div>

          <h1
            data-testid="printers-hero-headline"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tight text-white leading-[1.05] animate-fade-up"
            style={{ animationDelay: "80ms" }}
          >
            The Best Schools{" "}
            <span className="text-teal">Print In-House.</span>
            <br />
            Does Yours?
          </h1>

          <p
            className="mt-8 text-lg sm:text-xl text-slate-200 leading-relaxed max-w-2xl animate-fade-up"
            style={{ animationDelay: "160ms" }}
          >
            Choose the right Print Shop Pro system based on your school size,
            printing volume, and goals.
          </p>

          <div
            className="mt-10 animate-fade-up"
            style={{ animationDelay: "240ms" }}
          >
            <button
              data-testid="printers-hero-quote-btn"
              onClick={openQuote}
            title="Opens in a new tab"
              className="group inline-flex items-center justify-center gap-2 bg-teal hover:bg-[#0D6BB1] text-white font-bold px-8 py-4 rounded-lg shadow-xl hover:shadow-2xl transition-all duration-200 hover:-translate-y-0.5"
            >
              Request a Quote
              <ExternalLink className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </div>

          <ul
            className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 max-w-2xl animate-fade-up"
            style={{ animationDelay: "320ms" }}
          >
            {POINTS.map((p) => (
              <li key={p} className="flex items-start gap-3 text-slate-200">
                <span className="flex-shrink-0 mt-0.5 w-6 h-6 rounded-full bg-teal/20 border border-teal/40 flex items-center justify-center">
                  <Check className="w-3.5 h-3.5 text-teal" strokeWidth={3} />
                </span>
                <span className="text-sm font-medium">{p}</span>
              </li>
            ))}
          </ul>

          <div className="mt-10">
            <Link
              to="/"
              data-testid="printers-back-home-link"
              className="text-sm text-slate-300 hover:text-white underline underline-offset-4 transition-colors"
            >
              ← Back to homepage
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
